import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI, taxAPI, shippingDiscountAPI, paymentsAPI } from '../../utils/api';
import { useCartStore, useAuthStore } from '../../store';
import BraintreeDropIn from '../../components/common/BraintreeDropIn';
import { US_STATES } from '../../utils/constants';


const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, getTotal, clearCart } = useCartStore();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [guestMode, setGuestMode] = useState(false);

  // Payment method: always braintree
  const [paymentMethod] = useState('braintree');

  // Braintree state
  const [clientToken, setClientToken] = useState(null);
  const [btTokenLoading, setBtTokenLoading] = useState(false);
  const [btTokenError, setBtTokenError] = useState('');
  const [btReady, setBtReady] = useState(false); // Drop-in ready hai?
  const braintreeRef = useRef(null);

  // Address form
  const [form, setForm] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    pincode: '',
  });

  // State & tax
  const [stateCode, setStateCode] = useState('');
  const [taxInfo, setTaxInfo] = useState({ tax_rate: 0, tax_name: 'Sales Tax' });

  // Shipping discount
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);

  const subtotal = getTotal();
  const hasFullKit = items.some(i => i.kit_bundle);
  const stateShippingFee = taxInfo.shipping_fee != null ? parseFloat(taxInfo.shipping_fee) : 50;
  const baseShipping = hasFullKit ? 0 : stateShippingFee;

  // Fetch tax rate when state changes
  useEffect(() => {
    if (!stateCode) { setTaxInfo({ tax_rate: 0, tax_name: 'Sales Tax' }); return; }
    taxAPI.getByState(stateCode)
      .then(r => setTaxInfo(r.data))
      .catch(() => setTaxInfo({ tax_rate: 0, tax_name: 'Sales Tax' }));
  }, [stateCode]);

  // Fetch Braintree client token when payment method is braintree
  useEffect(() => {
    if (paymentMethod !== 'braintree') return;
    if (clientToken) return; // already fetched
    setBtTokenLoading(true);
    setBtTokenError('');
    paymentsAPI.getBraintreeClientToken()
      .then(r => setClientToken(r.data.clientToken))
      .catch(() => setBtTokenError('Could not load payment form. Please try again.'))
      .finally(() => setBtTokenLoading(false));
  }, [paymentMethod]);

  const shippingDiscount = (() => {
    if (!appliedDiscount) return 0;
    if (appliedDiscount.discount_type === 'free') return baseShipping;
    if (appliedDiscount.discount_type === 'percentage') return parseFloat((baseShipping * appliedDiscount.discount_value / 100).toFixed(2));
    if (appliedDiscount.discount_type === 'fixed') return Math.min(appliedDiscount.discount_value, baseShipping);
    return 0;
  })();

  const shippingAfterDiscount = Math.max(0, baseShipping - shippingDiscount);
  const taxRate = parseFloat(taxInfo.tax_rate || 0) / 100;
  const tax = parseFloat((subtotal * taxRate).toFixed(2));
  const total = subtotal + shippingAfterDiscount + tax;

  const [orderPlaced, setOrderPlaced] = useState(false);
  const [completedOrderNumber, setCompletedOrderNumber] = useState(null);

  // Navigate after order placed — state update ke baad
  useEffect(() => {
    if (completedOrderNumber) {
      navigate(`/orders/${completedOrderNumber}`, {
        state: { justPlaced: true, total, paymentMethod },
      });
    }
  }, [completedOrderNumber]);

  useEffect(() => {
    if (!orderPlaced && (items.length === 0 || subtotal < 25)) {
      navigate('/cart');
    }
  }, [items.length, subtotal, orderPlaced, navigate]);

  if (!orderPlaced && (items.length === 0 || subtotal < 25)) return null;


  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleApplyCode = async () => {
    if (!discountCode.trim()) return;
    setApplyingCode(true); setDiscountError(''); setAppliedDiscount(null);
    try {
      const res = await shippingDiscountAPI.applyCode(discountCode.trim(), subtotal);
      setAppliedDiscount(res.data);
    } catch (err) {
      setDiscountError(err.response?.data?.error || 'Invalid code');
    } finally {
      setApplyingCode(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.full_name || !form.address_line1 || !form.city || !form.pincode || !form.email) {
      setError('Please fill all required fields.'); return;
    }
    if (paymentMethod === 'braintree' && !braintreeRef.current) {
      setError('Payment form is not ready yet. Please wait a moment.'); return;
    }

    setLoading(true); setError('');

    try {
      const orderItems = items.map(i => ({
        product_id: i.product_id,
        kit_id: i.kit_id || null,
        qty: i.qty,
      }));

      // Step 1: Create the order
      const orderResult = await ordersAPI.create({
        items: orderItems,
        shipping_address: { ...form, state: stateCode },
        customer_email: form.email,
        payment_method: paymentMethod === 'braintree' ? 'Braintree' : 'COD',
        shipping_fee: baseShipping,
        shipping_discount_code: appliedDiscount?.code || null,
        state_code: stateCode || null,
      });

      const { order_id: orderId, order_number } = orderResult.data;
      console.log('Order created:', { orderId, order_number, orderResult: orderResult.data });

      // Step 2: If Braintree — get nonce and charge
      if (paymentMethod === 'braintree') {
        if (!braintreeRef.current) {
          throw new Error('Payment form not ready. Please refresh and try again.');
        }
        // Get payment method nonce from Drop-in UI
        const paymentMethodResult = await braintreeRef.current.requestPaymentMethod();
        console.log('Payment method result:', paymentMethodResult);
        const nonce = paymentMethodResult.nonce;

        if (!nonce) {
          throw new Error('Could not get payment nonce. Please try again.');
        }

        console.log('Sending to backend:', { order_id: orderId, nonce });
        // Charge via backend
        await paymentsAPI.processBraintreePayment({ order_id: orderId, nonce });
      }

      // Step 3: Set order placed — useEffect will navigate
      setOrderPlaced(true);
      clearCart();
      // Direct navigation — bypass React Router
      window.location.href = `/orders/${order_number}?justPlaced=true`;
    } catch (err) {
      // Braintree Drop-in error (e.g. card declined, validation)
      const message = err.response?.data?.error
        || err.message
        || 'Order placement failed. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ padding: '1.5rem 1rem' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1.5rem' }}>Checkout</h1>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="page-two-col">
        {/* Left: Address + Discount + Payment */}
        <div className="page-col-main">

          {/* Shipping Address */}
          <div className="card">
            <h2 style={{ fontWeight: 700, marginBottom: '1rem' }}>Shipping Address</h2>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" value={form.full_name} onChange={e => set('full_name', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Email Address *</label>
              <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@example.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Address Line 1 *</label>
              <input className="form-input" value={form.address_line1} onChange={e => set('address_line1', e.target.value)} placeholder="Address Line 1" />
            </div>
            <div className="form-group">
              <label className="form-label">Address Line 2</label>
              <input className="form-input" value={form.address_line2} onChange={e => set('address_line2', e.target.value)} placeholder="Address Line 2 (optional)" />
            </div>
            <div className="grid-3">
              <div className="form-group">
                <label className="form-label">City *</label>
                <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">State *</label>
                <select className="form-input" value={stateCode} onChange={e => { setStateCode(e.target.value); setAppliedDiscount(null); setDiscountCode(''); }}>
                  <option value="">-- Select State --</option>
                  {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Zipcode *</label>
                <input className="form-input" value={form.pincode} onChange={e => set('pincode', e.target.value)} maxLength={10} />
              </div>
            </div>
          </div>

          {/* Shipping Discount Code */}
          <div className="card" style={{ marginTop: '1rem' }}>
            <h2 style={{ fontWeight: 700, marginBottom: '0.75rem' }}>🚚 Shipping Discount Code</h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                className="form-input"
                value={discountCode}
                onChange={e => { setDiscountCode(e.target.value.toUpperCase()); setDiscountError(''); setAppliedDiscount(null); }}
                placeholder="Enter promo code (e.g. FREESHIP100)"
                style={{ flex: 1 }}
              />
              <button className="btn btn-outline" onClick={handleApplyCode} disabled={applyingCode || !discountCode.trim()}>
                {applyingCode ? '…' : 'Apply'}
              </button>
            </div>
            {discountError && <p style={{ color: '#dc2626', fontSize: '0.82rem', marginTop: '0.4rem' }}>{discountError}</p>}
            {appliedDiscount && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: '#dcfce7', borderRadius: 6, color: '#166534', fontSize: '0.85rem', fontWeight: 600 }}>
                ✓ {appliedDiscount.name} applied
                {shippingDiscount > 0 && ` — saves $${shippingDiscount.toFixed(2)} on shipping`}
                {shippingAfterDiscount === 0 && ' (FREE shipping!)'}
              </div>
            )}
          </div>

          {/* Payment Method */}
          <div className="card" style={{ marginTop: '1rem' }}>
            <h2 style={{ fontWeight: 700, marginBottom: '0.75rem' }}>Payment Method</h2>

            {/* Braintree Drop-in UI */}
            <div style={{ width: '100%', overflow: 'hidden' }}>
              {btTokenLoading && (
                <div style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>
                  Loading secure payment form...
                </div>
              )}
              {btTokenError && (
                <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>
                  {btTokenError}
                  <button
                    className="btn btn-outline"
                    style={{ marginLeft: '0.75rem', padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}
                    onClick={() => { setBtTokenError(''); setClientToken(null); setBtReady(false); braintreeRef.current = null; }}
                  >
                    Retry
                  </button>
                </div>
              )}
              {clientToken && !btTokenError && (
                <BraintreeDropIn
                  clientToken={clientToken}
                  onReady={(instance) => {
                    braintreeRef.current = instance;
                    setBtReady(true);
                  }}
                  onError={(err) => {
                    setBtTokenError('Payment form error: ' + err.message);
                    setBtReady(false);
                    braintreeRef.current = null;
                  }}
                />
              )}
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                🔒 Your payment information is encrypted and secure.
              </p>
            </div>
          </div>
        </div>

        {/* Right: Order Summary */}
        <div className="page-col-side">
          <div className="card">
            <h2 style={{ fontWeight: 700, marginBottom: '1rem' }}>Order Summary</h2>
            {items.map(item => (
              <div key={item._key} style={{ padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span>{item.name} × {item.qty}</span>
                  <span style={{ fontWeight: 600 }}>${(item.price * item.qty).toFixed(2)}</span>
                </div>
                {item.kit_bundle && item.kit_items && item.kit_items.length > 0 && (
                  <div style={{ paddingLeft: '0.5rem', marginTop: '0.25rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                    {item.kit_items.map(sub => (
                      <div key={sub.product_id} style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center' }}>
                        <span style={{ width: '16px' }}>-</span>
                        <span style={{ flex: 1 }}>{sub.default_qty}x {sub.name}</span>
                        {item.selectedColors && item.selectedColors[sub.product_id] && item.selectedColors[sub.product_id].length > 0 && (
                          <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>({item.selectedColors[sub.product_id].join(', ')})</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.9rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Shipping</span>
                <span>
                  {baseShipping === 0
                    ? 'FREE'
                    : appliedDiscount && shippingAfterDiscount < baseShipping
                      ? <><s style={{ color: '#9ca3af', marginRight: 4 }}>${baseShipping.toFixed(2)}</s>{shippingAfterDiscount === 0 ? 'FREE' : `$${shippingAfterDiscount.toFixed(2)}`}</>
                      : `$${baseShipping.toFixed(2)}`}
                </span>
              </div>
              {appliedDiscount && shippingDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#16a34a', fontSize: '0.82rem' }}>
                  <span>Shipping discount ({appliedDiscount.code})</span>
                  <span>−${shippingDiscount.toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{taxInfo.tax_name || 'Tax'} ({parseFloat(taxInfo.tax_rate || 0).toFixed(1)}%)</span>
                <span>${tax.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1.1rem', borderTop: '2px solid #e5e7eb', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
                <span>Total</span><span>${total.toFixed(2)}</span>
              </div>
            </div>

            <button
              className="btn btn-primary btn-full btn-lg"
              style={{ marginTop: '1.25rem' }}
              onClick={handleSubmit}
              disabled={loading || (paymentMethod === 'braintree' && (btTokenLoading || !!btTokenError || !btReady))}
            >
              {loading
                ? (paymentMethod === 'braintree' ? 'Processing Payment...' : 'Placing Order...')
                : (paymentMethod === 'braintree' ? '🔒 Pay Now' : '✓ Place Order')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
